import type { FieldProp, FormObject } from 'react-typed-form';
import type { ServiceOrderFormValues } from '../service-order-form';

import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { FieldText } from 'src/form';

type Props = {
  form: FormObject<ServiceOrderFormValues>;
  companyName: string | null;
  projectName: string | null;
};

/**
 * The details of a service order: what it is called and worth, and which
 * client and project it belongs to.  The client and project come with the
 * screen and cannot be changed here.
 */
export default function ServiceOrderDetailsCard({
  form,
  companyName,
  projectName,
}: Props) {
  return (
    <html.div style={[Style.col, styles.card]}>
      <Text typeface="body.small.medium" color="neutral100" as="h2">
        Details
      </Text>

      <html.div style={[Style.row, styles.row]}>
        <html.div style={styles.fieldGrow}>
          <FieldText label="Name" field={form.getField('name')} />
        </html.div>
        <html.div style={styles.fieldRef}>
          <FieldText
            label="Reference"
            field={form.getField('referenceNumber')}
            placeholder="2026-006"
          />
        </html.div>
      </html.div>

      <html.div style={[Style.row, styles.row]}>
        <html.div style={styles.fieldHalf}>
          <ReadOnlyField label="Client" value={companyName} />
        </html.div>
        <html.div style={styles.fieldHalf}>
          <ReadOnlyField label="Project" value={projectName} />
        </html.div>
      </html.div>

      <html.div style={[Style.row, styles.row]}>
        <html.div style={styles.fieldThird}>
          <FeeField field={form.getField('budget')} />
        </html.div>
        <html.div style={styles.fieldThird}>
          <FieldText label="Day limit" field={form.getField('dayLimit')} />
        </html.div>
        <html.span style={Style.fill} />
      </html.div>

      <Text typeface="body.small.regular" color="neutral60">
        Orders with no project belong to the client alone. Fee is agency-only;
        day limit is for everyone.
      </Text>
    </html.div>
  );
}

type ReadOnlyProps = {
  label: string;
  value: string | null;
};

function ReadOnlyField({ label, value }: ReadOnlyProps) {
  return (
    <html.div style={[Style.col, styles.field]}>
      <Text typeface="body.small.regular" color="neutral60">
        {label}
      </Text>
      <html.input
        style={[styles.input, styles.inputDisabled]}
        value={value ?? ''}
        disabled
      />
    </html.div>
  );
}

/** The fee, shown dressed as money but stored as plain pounds. */
function FeeField({ field }: { field: FieldProp<string | null> }) {
  const shown = !field.value
    ? ''
    : `£ ${Number(field.value).toLocaleString('en-GB')}`;

  return (
    <html.div style={[Style.col, styles.field]}>
      <Text
        typeface="body.small.regular"
        color={field.isLoading ? 'neutral60' : 'neutral80'}
      >
        Fee
      </Text>
      <html.input
        style={[styles.input, field.isLoading && styles.inputDisabled]}
        value={shown}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          field.handleValueChange(e.target.value.replace(/[^\d]/g, ''))
        }
        disabled={field.isLoading}
        inputMode="numeric"
        placeholder="£ 0"
      />
    </html.div>
  );
}

const styles = css.create({
  card: {
    gap: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    backgroundColor: color.neutral0,
  },
  row: {
    gap: 12,
  },
  field: {
    gap: 8,
  },
  fieldGrow: {
    flexGrow: 1,
    flexBasis: 0,
  },
  fieldRef: {
    flexShrink: 0,
    width: {
      default: 200,
      '@media (max-width: 640px)': '100%',
    },
  },
  fieldHalf: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  fieldThird: {
    flexShrink: 0,
    width: {
      default: 240,
      '@media (max-width: 640px)': '100%',
    },
  },
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: color.neutral0,
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
  inputDisabled: {
    backgroundColor: color.neutral15,
    color: color.neutral60,
    cursor: 'default',
  },
});
